import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, ArrowRight, CalendarDays } from "lucide-react";
import { format } from "date-fns";
import { da } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CATEGORIES = [
  "IT-support & reparation",
  "HiFi & lydanlæg",
  "Smart home & hjemmeelektronik",
  "3D-print",
  "Robotplæneklipper",
  "Serviceaftale",
  "Andet",
];

export default function Booking() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, refresh } = useAuth();
  const [plans, setPlans] = useState([]);
  const [discount, setDiscount] = useState(25);
  const [form, setForm] = useState({
    name: user?.name || "", email: user?.email || "", phone: user?.phone || "",
    category: "", device: "", description: "", preferred_date: "",
    plan_years: "", billing: "maanedlig", create_account: false, password: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get("/service-plans").then(({ data }) => {
      setPlans(data?.plans || []);
      if (data?.annual_discount_pct != null) setDiscount(data.annual_discount_pct);
      const aftale = searchParams.get("aftale");
      const betaling = searchParams.get("betaling");
      if (aftale) {
        setForm((f) => ({
          ...f, category: "Serviceaftale", plan_years: String(aftale),
          billing: betaling === "aarlig" ? "aarlig" : "maanedlig",
        }));
      }
    }).catch(() => {});
    // eslint-disable-next-line
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const selectedPlan = plans.find((p) => String(p.years) === String(form.plan_years));
  const annualPerYear = (m) => Math.round(m * 12 * (1 - discount / 100));

  const planPriceText = () => {
    if (!selectedPlan) return "";
    if (form.billing === "aarlig")
      return `${annualPerYear(selectedPlan.monthly_price)} kr./år (årlig betaling, spar ${discount}%)`;
    return `${selectedPlan.monthly_price} kr./md (månedlig betaling)`;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.category) { toast.error("Vælg venligst en kategori"); return; }
    if (form.category === "Serviceaftale" && !form.plan_years) { toast.error("Vælg venligst en aftalelængde"); return; }
    setLoading(true);
    try {
      const payload = { ...form };
      if (form.category === "Serviceaftale" && selectedPlan) {
        payload.description =
          `Ønsket serviceaftale: ${selectedPlan.years} år – ${planPriceText()}\n\n${form.description}`;
      }
      delete payload.plan_years;
      delete payload.billing;
      const { data } = await api.post("/bookings", payload);
      if (data.account_created) await refresh();
      setDone(true);
      toast.success("Din booking er sendt!");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Kunne ikke sende booking");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col">
        <Navbar />
        <div className="flex-1 grid place-items-center p-6">
          <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 text-center" data-testid="booking-success">
            <span className="mx-auto grid place-items-center h-14 w-14 rounded-full bg-blue-50 text-blue-600">
              <CheckCircle2 className="h-7 w-7" />
            </span>
            <h1 className="mt-5 text-2xl font-display font-black tracking-tight text-slate-900">Tak for din booking!</h1>
            <p className="mt-2 text-slate-600">
              Jeg har modtaget din forespørgsel og vender tilbage hurtigst muligt.
              {form.create_account || user ? " Du kan følge status i din kundeportal." : ""}
            </p>
            <div className="mt-6 flex flex-col gap-2">
              {(form.create_account || user) && (
                <Button onClick={() => navigate(user ? "/portal" : "/login")} data-testid="booking-to-portal"
                  className="w-full h-11 rounded-lg bg-blue-600 hover:bg-blue-700">
                  Til kundeportal <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              )}
              <Button variant="outline" onClick={() => navigate("/")} className="w-full h-11 rounded-lg">Til forsiden</Button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <Navbar />
      <main className="flex-1 max-w-3xl w-full mx-auto px-5 sm:px-8 py-12">
        <p className="text-sm font-semibold uppercase tracking-widest text-blue-600">Booking</p>
        <h1 className="mt-2 text-3xl sm:text-4xl font-display font-black tracking-tighter text-slate-900">
          Book en reparation eller service
        </h1>
        <p className="mt-3 text-slate-600">
          Beskriv dit behov, så vender jeg tilbage med en plan. <span className="font-semibold text-slate-900">Følger DIT behov.</span>
        </p>

        <form onSubmit={submit} className="mt-8 rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 space-y-5">
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="space-y-1.5">
              <Label htmlFor="bname">Navn *</Label>
              <Input id="bname" required value={form.name} onChange={set("name")} data-testid="booking-name"
                className="h-11 bg-slate-50 focus-visible:ring-blue-600" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="bemail">E-mail *</Label>
              <Input id="bemail" type="email" required value={form.email} onChange={set("email")} data-testid="booking-email"
                className="h-11 bg-slate-50 focus-visible:ring-blue-600" />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            <div className="space-y-1.5">
              <Label htmlFor="bphone">Telefon</Label>
              <Input id="bphone" value={form.phone} onChange={set("phone")} data-testid="booking-phone"
                className="h-11 bg-slate-50 focus-visible:ring-blue-600" />
            </div>
            <div className="space-y-1.5">
              <Label>Kategori *</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="booking-category" className="h-11 bg-slate-50 focus:ring-blue-600">
                  <SelectValue placeholder="Vælg kategori" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {form.category === "Serviceaftale" && (
            <div className="space-y-4 rounded-xl border border-blue-100 bg-blue-50/60 p-4">
              <div className="space-y-1.5">
                <Label>Vælg aftalelængde *</Label>
                <Select value={form.plan_years} onValueChange={(v) => setForm({ ...form, plan_years: v })}>
                  <SelectTrigger data-testid="booking-plan" className="h-11 bg-white focus:ring-blue-600">
                    <SelectValue placeholder="Vælg pakke (2, 3 eller 5 år)" />
                  </SelectTrigger>
                  <SelectContent>
                    {plans.map((p) => (
                      <SelectItem key={p.id} value={String(p.years)}>
                        {p.years} år – {p.monthly_price} kr./md
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label>Betaling</Label>
                <div className="flex gap-3">
                  {[["maanedlig", "Månedlig", ""], ["aarlig", "Årlig", `spar ${discount}%`]].map(([v, l, sub]) => (
                    <button type="button" key={v} onClick={() => setForm({ ...form, billing: v })}
                      data-testid={`booking-billing-${v}`}
                      className={`flex-1 rounded-lg border px-4 py-3 text-sm font-medium transition-colors ${
                        form.billing === v ? "border-blue-600 bg-blue-50 text-blue-700" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                      }`}>
                      {l}{sub && <span className="block text-xs text-blue-600 mt-0.5">{sub}</span>}
                    </button>
                  ))}
                </div>
              </div>

              {selectedPlan && (
                <div className="rounded-lg bg-white border border-blue-100 px-4 py-3 text-sm" data-testid="booking-plan-summary">
                  <span className="text-slate-500">Din pris: </span>
                  <span className="font-semibold text-slate-900">{planPriceText()}</span>
                </div>
              )}
              <p className="text-xs text-blue-700">
                Gælder nyt og brugt udstyr. Ved årlig betaling sparer du {discount}%.{" "}
                <a href="/betingelser" target="_blank" rel="noreferrer" data-testid="booking-terms-link" className="font-semibold underline">Læs betingelser</a>
              </p>
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-5">
            <div className="space-y-1.5">
              <Label htmlFor="bdevice">Enhed / model</Label>
              <Input id="bdevice" value={form.device} onChange={set("device")} data-testid="booking-device"
                placeholder="fx Lenovo bærbar, Sonos højttaler" className="h-11 bg-slate-50 focus-visible:ring-blue-600" />
            </div>
            <div className="space-y-1.5">
              <Label>Ønsket dato</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button type="button" variant="outline" data-testid="booking-date"
                    className="h-11 w-full justify-start bg-slate-50 font-normal hover:bg-slate-50">
                    <CalendarDays className="mr-2 h-4 w-4 text-slate-500" />
                    {form.preferred_date
                      ? <span className="text-slate-900">{format(new Date(form.preferred_date), "dd-MM-yyyy", { locale: da })}</span>
                      : <span className="text-slate-400">Vælg en dato</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" locale={da} weekStartsOn={1}
                    selected={form.preferred_date ? new Date(form.preferred_date) : undefined}
                    onSelect={(d) => setForm({ ...form, preferred_date: d ? format(d, "yyyy-MM-dd") : "" })}
                    disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                    initialFocus />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="bdesc">Beskriv dit behov *</Label>
            <Textarea id="bdesc" required rows={4} value={form.description} onChange={set("description")} data-testid="booking-description"
              placeholder="Fortæl hvad der driller, eller hvad du gerne vil have hjælp til…"
              className="bg-slate-50 focus-visible:ring-blue-600 resize-none" />
          </div>

          {!user && (
            <div className="rounded-xl border border-blue-100 bg-blue-50/60 p-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox checked={form.create_account} data-testid="booking-create-account"
                  onCheckedChange={(v) => setForm({ ...form, create_account: !!v })} className="mt-0.5" />
                <span className="text-sm text-slate-700">
                  Opret en kundekonto til mig — så kan jeg følge min booking og skrive til dig.
                  <span className="block text-xs text-blue-700 mt-0.5">Adgang godkendes automatisk, når du booker.</span>
                </span>
              </label>
              {form.create_account && (
                <div className="mt-3 space-y-1.5">
                  <Label htmlFor="bpass">Vælg en adgangskode</Label>
                  <Input id="bpass" type="password" value={form.password} onChange={set("password")} data-testid="booking-password"
                    placeholder="min. 6 tegn" className="h-11 bg-white focus-visible:ring-blue-600" />
                </div>
              )}
            </div>
          )}

          <Button type="submit" disabled={loading} data-testid="booking-submit"
            className="w-full h-12 rounded-full bg-blue-600 hover:bg-blue-700 text-base shadow-[0_0_24px_rgba(37,99,235,0.35)]">
            {loading ? "Sender…" : "Send booking"}
          </Button>
        </form>
      </main>
      <Footer />
    </div>
  );
}
